import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { BehaviorSubject } from 'rxjs';
import { StorageService } from '../core/storage.service';
import { Month } from '../shared/models/month.model';
import { LogedUser } from '../core/models/logged-user.model';
import { UserSessionService } from './user-session.service';
import axios from 'axios';

type InputTypes = string | number | undefined;

@Injectable({
  providedIn: 'root',
})
export class UtilsService {
  constructor(private readonly Storage: StorageService) {}

  public static readonly defaultGuid = '00000000-0000-0000-0000-000000000000';

  months: Month[] = [
    {
      number: 1,
      name: 'January',
      spanishName: 'Enero',
      abbreviation: 'jan',
      spanishAbbreviation: 'ene',
      period: '01',
    },
    {
      number: 2,
      name: 'February',
      spanishName: 'Febrero',
      abbreviation: 'feb',
      spanishAbbreviation: 'feb',
      period: '02',
    },
    {
      number: 3,
      name: 'March',
      spanishName: 'Marzo',
      abbreviation: 'mar',
      spanishAbbreviation: 'mar',
      period: '03',
    },
    {
      number: 4,
      name: 'April',
      spanishName: 'Abril',
      abbreviation: 'apr',
      spanishAbbreviation: 'abr',
      period: '04',
    },
    {
      number: 5,
      name: 'May',
      spanishName: 'Mayo',
      abbreviation: 'may',
      spanishAbbreviation: 'may',
      period: '05',
    },
    {
      number: 6,
      name: 'June',
      spanishName: 'Junio',
      abbreviation: 'jun',
      spanishAbbreviation: 'jun',
      period: '06',
    },
    {
      number: 7,
      name: 'July',
      spanishName: 'Julio',
      abbreviation: 'jul',
      spanishAbbreviation: 'jul',
      period: '07',
    },
    {
      number: 8,
      name: 'August',
      spanishName: 'Agosto',
      abbreviation: 'aug',
      spanishAbbreviation: 'ago',
      period: '08',
    },
    {
      number: 9,
      name: 'September',
      spanishName: 'Septiembre',
      abbreviation: 'sep',
      spanishAbbreviation: 'sep',
      period: '09',
    },
    {
      number: 10,
      name: 'October',
      spanishName: 'Octubre',
      abbreviation: 'oct',
      spanishAbbreviation: 'oct',
      period: '10',
    },
    {
      number: 11,
      name: 'November',
      spanishName: 'Noviembre',
      abbreviation: 'nov',
      spanishAbbreviation: 'nov',
      period: '11',
    },
    {
      number: 12,
      name: 'December',
      spanishName: 'Diciembre',
      abbreviation: 'dec',
      spanishAbbreviation: 'dic',
      period: '12',
    },
  ];

  static getGuid(): any {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
      /[xy]/g,
      function (c) {
        const r = (Math.random() * 16) | 0,
          v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      },
    );
  }
  // Pasar data desde child a grandparent, por ejemplo.
  private readonly childEvent = new BehaviorSubject<any>('');
  emitChildEvent(msg: any): void {
    this.childEvent.next(msg);
  }

  childEventListner(): any {
    return this.childEvent.asObservable();
  }

  // Formatear un numero con '.' ejemplo: 2000 a 2.000
  public formatNumberWithDot = (num: any) => {
    const VALUE = num ?? 0;
    return VALUE.toString()
      .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')
      .replace(',', '.');
  };

  processNumberAddDotFormat(valueRaw: InputTypes): string {
    let valueParsed: string = '-';
    if (valueRaw) {
      let valueNoDots: string[] = valueRaw.toString().split('.');
      valueParsed = valueNoDots[0];
    }
    return valueRaw
      ? valueParsed.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
      : '-';
  }

  cleanNumberForWsp(valueRaw: InputTypes): string {
    let valueParsed: string = '-';
    if (valueRaw) valueParsed = valueRaw.toString();
    return valueRaw
      ? valueParsed
          .toString()
          .replace(/\B(?=(\d{3})+(?!\d))/g, '')
          .replace(/[()]/g, '')
      : '-';
  }

  //Normalizar un texto para búsquedas: normaliza mayusculas, minusculas, tildes y ü
  public textNormalize(text: InputTypes): string {
    let t = text?.toString().toLowerCase();
    return t
      ? t
          .normalize('NFD')
          .replace(/[\u0300-\u0302\u0308]/g, '') //Elimina tildes y ü (excepto "ñ")
          .normalize()
      : '';
  }

  // Formatear puntos por comas (decimales)
  public formatDecimalWithComma = (num: any) => {
    const VALUE = num ?? 0;
    return VALUE.toString()
      .replace('.', ',')
      .replace(/[.,]00$/, '');
  };

  // Cortar string al tener un numero mayor de caracteres y agregar '...' a lo ultimo
  public truncateString = (string: any, maxlength: any) => {
    if (string.length > maxlength)
      return string.substring(0, maxlength) + '...';
    else return string;
  };

  // Cortar string al tener un numero mayor de caracteres y agregar '...' a lo ultimo
  public truncateStringNoDots = (string: any, maxlength: any) => {
    let input = string;
    if (typeof string != 'string') {
      input = string.toString();
    }
    if (input.length > maxlength) return input.substring(0, maxlength);
    else return input;
  };

  // Redondear numeros decimales
  public roundToDecimal = (number: any) => {
    const float = parseFloat(number);
    const result = Math.round(float * 100) / 100;
    return result;
  };

  // Redondedear a número entero más cercano.
  public roundToNearInteger = (number: any) => {
    return Math.round(number);
  };

  // Retorna el entero más cercano  y remueve cualquier dígito fraccionario.
  public roundWithoutDecimal = (number: any) => {
    return Math.trunc(Math.round(number));
  };

  // Retorna el número sin decimales
  public numberWithoutDecimal = (number: any) => {
    return Math.trunc(number);
  };

  isInternalUrl(url: any): boolean {
    return (
      !url.includes('develop-redremax.com') && !url.includes('redremax.com')
    );
  }

  // Cambiar imagen cuando esta caida/eliminada
  public manageErrorImage = (event: any) => {
    event.target.src = '/assets/img/default-img.svg';
  };

  public getFormattedDate = (dateToFormat: string | undefined): string => {
    return dateToFormat ? moment(dateToFormat).format('DD/MM/YYYY') : '-';
  };

  public getMonthNumber(month: string): string {
    return this.months.find((m) => m.spanishName === month)?.period ?? '';
  }

  public getMonths(): string[] {
    return [
      'Enero',
      'Febrero',
      'Marzo',
      'Abril',
      'Mayo',
      'Junio',
      'Julio',
      'Agosto',
      'Septiembre',
      'Octubre',
      'Noviembre',
      'Diciembre',
    ];
  }

  public getMonthsAbr(): string[] {
    return [
      'Ene',
      'Feb',
      'Mar',
      'Abr',
      'May',
      'Jun',
      'Jul',
      'Ago',
      'Sep',
      'Oct',
      'Nov',
      'Dic',
    ];
  }

  public getYears(startYear: number, formatToSelect?: boolean): any {
    const years = [];
    const actualYear = Number(moment().format('YYYY'));

    for (let y = actualYear; y >= startYear; y--) {
      if (formatToSelect) {
        const item = { value: y, text: y };
        years.push(item);
      } else {
        years.push(y);
      }
    }
    return years;
  }

  public capitalize(string: string) {
    if (!string) {
      return string;
    }
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
  }

  public parseGoogleAddress = (googleAddress: any): any => {
    const response: any = {
      display_address: googleAddress.formatted_address,
      postal_code: null,
      street_number: null,
      street_name: null,
      neighborhood: null,
      city: null,
      city_id: null,
      county: null,
      county_id: null,
      state: null,
      state_id: null,
      country: null,
      country_id: null,
      lat: null,
      lng: null,
    };

    const relations = {
      street_number: 'street_number',
      street_name: 'route',
      neighborhood: 'neighborhood',
      neighborhood2: 'sublocality_level_1',
      county: 'administrative_area_level_2',
      state: 'administrative_area_level_1',
      country: 'country',
      postal_code: 'postal_code',
      city: 'locality',
    };

    response.country = this.searchTypeInGoogleAddress(
      relations.country,
      googleAddress.address_components,
      true,
    );
    response.country_id = this.searchTypeInGoogleAddress(
      relations.country,
      googleAddress.address_components,
      false,
    );
    response.state_id = this.searchTypeInGoogleAddress(
      relations.state,
      googleAddress.address_components,
      false,
    );
    response.state = this.searchTypeInGoogleAddress(
      relations.state,
      googleAddress.address_components,
      true,
    );
    const stateShort = this.searchTypeInGoogleAddress(
      relations.state,
      googleAddress.address_components,
      false,
    );
    response.county = this.searchTypeInGoogleAddress(
      relations.county,
      googleAddress.address_components,
      false,
    );
    response.county_id = this.searchTypeInGoogleAddress(
      relations.county,
      googleAddress.address_components,
      true,
    );
    response.street_number = this.searchTypeInGoogleAddress(
      relations.street_number,
      googleAddress.address_components,
      true,
    );
    response.street_name = this.searchTypeInGoogleAddress(
      relations.street_name,
      googleAddress.address_components,
      true,
    );
    response.postal_code = this.searchTypeInGoogleAddress(
      relations.postal_code,
      googleAddress.address_components,
      true,
    );

    if (stateShort == 'CABA') {
      response.state = 'Capital Federal';
      if (response?.county.toLowerCase().includes('comuna')) {
        const countyPart = response.county.split(' ');
        response.county = countyPart[0] + ' Nro. ' + countyPart[1];
      }
      relations.city = 'sublocality_level_1';
    }

    response.neighborhood = this.searchTypeInGoogleAddress(
      relations.neighborhood,
      googleAddress.address_components,
      true,
    );

    response.neighborhood ??= this.searchTypeInGoogleAddress(
      relations.neighborhood2,
      googleAddress.address_components,
      true,
    );
    response.city = this.searchTypeInGoogleAddress(
      relations.city,
      googleAddress.address_components,
      true,
    );
    response.city_id = this.searchTypeInGoogleAddress(
      relations.city,
      googleAddress.address_components,
      true,
    );
    if (typeof googleAddress.geometry.location.lat == 'function') {
      response.lat = googleAddress.geometry.location.lat();
      response.lng = googleAddress.geometry.location.lng();
    } else {
      response.lat = googleAddress.geometry.location.lat;
      response.lng = googleAddress.geometry.location.lng;
    }
    return response;
  };

  // parseFormattedAddressAndGeometry: Para buscador autocomplete donde solo se necesita displayAddress, lat y lng
  public parseFormattedAddressAndGeometry = (googleAddress: any): any => {
    const response = {
      display_address: googleAddress.formatted_address,
      lat: null,
      lng: null,
    };
    if (typeof googleAddress.geometry.location.lat == 'function') {
      response.lat = googleAddress.geometry.location.lat();
      response.lng = googleAddress.geometry.location.lng();
    } else {
      response.lat = googleAddress.geometry.location.lat;
      response.lng = googleAddress.geometry.location.lng;
    }
    return response;
  };

  private readonly searchTypeInGoogleAddress = (
    type: string,
    googleAddress: any,
    longName: boolean,
  ): any => {
    let response: any = null;
    googleAddress.forEach((element: any) => {
      // Número
      if (element.types.indexOf(type) >= 0) {
        if (longName) response = element.long_name;
        else response = element.short_name;
      }
    });
    if (response !== null) {
      if (type == 'administrative_area_level_1') {
        response = response.replace('Provincia de ', '');
        response = response.replace('Provincia de ', '');
      }
      response = response.replace('Partido de ', '');
    }
    return response;
  };

  getLoggedUser(): LogedUser | undefined {
    let LOGGED_USER: LogedUser | undefined;
    const LOGGED_USER_JSON: string | null = localStorage.getItem(
      UserSessionService.LOGGED_USER,
    );
    if (LOGGED_USER_JSON) {
      LOGGED_USER = JSON.parse(LOGGED_USER_JSON);
    }
    return LOGGED_USER;
  }

  public sanitizeFileName(fileName: string): string {
    return fileName
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9().\-_]/g, '_')
      .replace(/_+/g, '_')
      .replace(/(^_|_$)/g, '')
      .trim();
  }

  async auditMonitor(body: any, privateKey: string) {
    const URL = 'https://us-central1-qr-logs.cloudfunctions.net/createlogcors';
    const TOKEN = await this.generateJWTToken(privateKey);

    const http = axios.create({
      timeout: 50000,
      headers: {
        'Content-Type': 'application/json',
        Authorization: TOKEN ? `Bearer ${TOKEN}` : '',
        'Access-Control-Allow-Origin': '*',
        'Access-Control-Allow-Credentials': 'true',
        'Access-Control-Allow-Headers':
          'Origin,Content-Type,X-Amz-Date,Authorization,X-Api-Key,X-Amz-Security-Token,locale',
        'Access-Control-Allow-Methods':
          'GET, POST, PUT, PATCH, DELETE, OPTIONS',
      },
    });

    http.post(URL, body);
  }

  private async generateJWTToken(privateKey: string): Promise<string> {
    const HEADER = {
      alg: 'HS256',
      typ: 'JWT',
    };

    const NOW = Math.floor(Date.now() / 1000);
    const PAYLOAD = {
      exp: NOW + 30, // Expira en 30 segundos
      iat: NOW,
    };

    const base64UrlEncode = (str: string): string => {
      return btoa(str)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '');
    };

    const ENCODED_HEADER = base64UrlEncode(JSON.stringify(HEADER));
    const ENCODED_PAYLOAD = base64UrlEncode(JSON.stringify(PAYLOAD));
    const SIGNATURE_INPUT = `${ENCODED_HEADER}.${ENCODED_PAYLOAD}`;

    // Crear la firma usando Web Crypto API
    const ENCODER = new TextEncoder();
    const KEY_DATA = ENCODER.encode(privateKey);
    const MESSAGE_DATA = ENCODER.encode(SIGNATURE_INPUT);

    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      KEY_DATA,
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign'],
    );

    const SIGNATURE = await crypto.subtle.sign('HMAC', cryptoKey, MESSAGE_DATA);
    const SIGNATURE_ARRAY = Array.from(new Uint8Array(SIGNATURE));
    const ENCODED_SIGNATURE = base64UrlEncode(
      String.fromCharCode(...SIGNATURE_ARRAY),
    );

    return `${SIGNATURE_INPUT}.${ENCODED_SIGNATURE}`;
  }
}
